import {
  toHp,
  toSeatIndex,
  toTeamIndex,
  toTick,
  toUnits,
} from "./branded-schemas.js";
import type { EntityId, StateHash } from "./brands.js";
import type {
  ServerDeltaMessage,
  WireEntitiesDelta,
  WireEntityDelta,
  WireProjectilesDelta,
  WireSimState,
  WireWallsDelta,
} from "./messages.js";

function areCooldownsEqual(
  a: WireSimState["entities"][number]["cooldowns"],
  b: WireSimState["entities"][number]["cooldowns"],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ca = a[i];
    const cb = b[i];
    if (!ca || !cb) return false;
    if (ca.slot !== cb.slot || ca.readyAtTick !== cb.readyAtTick) return false;
    if (ca.charges === null && cb.charges === null) continue;
    if (ca.charges === null || cb.charges === null) return false;
    if (
      ca.charges.available !== cb.charges.available ||
      ca.charges.nextChargeReadyAtTick !== cb.charges.nextChargeReadyAtTick
    ) {
      return false;
    }
  }
  return true;
}

function areTargetsEqual(
  a: WireSimState["entities"][number]["targets"],
  b: WireSimState["entities"][number]["targets"],
): boolean {
  return a.hostile === b.hostile && a.friendly === b.friendly;
}

function areAurasEqual(
  a: WireSimState["entities"][number]["auras"],
  b: WireSimState["entities"][number]["auras"],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const aa = a[i];
    const ab = b[i];
    if (!aa || !ab) return false;
    if (
      aa.def !== ab.def ||
      aa.sourceSeat !== ab.sourceSeat ||
      aa.appliedAtTick !== ab.appliedAtTick ||
      aa.expiresAtTick !== ab.expiresAtTick ||
      aa.stacks !== ab.stacks ||
      aa.absorbRemaining !== ab.absorbRemaining ||
      aa.polarity !== ab.polarity
    ) {
      return false;
    }
  }
  return true;
}

function areCastsEqual(
  a: WireSimState["entities"][number]["cast"],
  b: WireSimState["entities"][number]["cast"],
): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (
    a.slot !== b.slot ||
    a.startTick !== b.startTick ||
    a.endTick !== b.endTick ||
    a.target !== b.target
  ) {
    return false;
  }
  if (a.aimPoint === null && b.aimPoint === null) return true;
  if (a.aimPoint === null || b.aimPoint === null) return false;
  return a.aimPoint.x === b.aimPoint.x && a.aimPoint.y === b.aimPoint.y;
}

export function diffWireEntity(
  prev: WireSimState["entities"][number] | undefined,
  curr: WireSimState["entities"][number],
): WireEntityDelta | null {
  if (!prev) {
    return {
      entityId: curr.entityId,
      seat: curr.seat,
      team: curr.team,
      x: curr.x,
      y: curr.y,
      facing: curr.facing,
      hp: curr.hp,
      maxHp: curr.maxHp,
      alive: curr.alive,
      cast: curr.cast,
      cooldowns: curr.cooldowns,
      gcdReadyAtTick: curr.gcdReadyAtTick,
      targets: curr.targets,
      auras: curr.auras,
      resource: curr.resource,
      points: curr.points,
      radius: curr.radius,
      championId: curr.championId,
    };
  }

  const delta: WireEntityDelta = { entityId: curr.entityId };
  let changed = false;

  if (prev.seat !== curr.seat) {
    delta.seat = curr.seat;
    changed = true;
  }
  if (prev.team !== curr.team) {
    delta.team = curr.team;
    changed = true;
  }
  if (prev.x !== curr.x) {
    delta.x = curr.x;
    changed = true;
  }
  if (prev.y !== curr.y) {
    delta.y = curr.y;
    changed = true;
  }
  if (prev.facing !== curr.facing) {
    delta.facing = curr.facing;
    changed = true;
  }
  if (prev.hp !== curr.hp) {
    delta.hp = curr.hp;
    changed = true;
  }
  if (prev.maxHp !== curr.maxHp) {
    delta.maxHp = curr.maxHp;
    changed = true;
  }
  if (prev.alive !== curr.alive) {
    delta.alive = curr.alive;
    changed = true;
  }
  if (!areCastsEqual(prev.cast, curr.cast)) {
    delta.cast = curr.cast;
    changed = true;
  }
  if (!areCooldownsEqual(prev.cooldowns, curr.cooldowns)) {
    delta.cooldowns = curr.cooldowns;
    changed = true;
  }
  if (prev.gcdReadyAtTick !== curr.gcdReadyAtTick) {
    delta.gcdReadyAtTick = curr.gcdReadyAtTick;
    changed = true;
  }
  if (!areTargetsEqual(prev.targets, curr.targets)) {
    delta.targets = curr.targets;
    changed = true;
  }
  if (!areAurasEqual(prev.auras, curr.auras)) {
    delta.auras = curr.auras;
    changed = true;
  }
  if (prev.resource !== curr.resource) {
    delta.resource = curr.resource;
    changed = true;
  }
  if (prev.points !== curr.points) {
    delta.points = curr.points;
    changed = true;
  }
  if (prev.radius !== curr.radius) {
    delta.radius = curr.radius;
    changed = true;
  }
  if (prev.championId !== curr.championId) {
    delta.championId = curr.championId;
    changed = true;
  }

  return changed ? delta : null;
}

export function diffEntities(
  prev: WireSimState["entities"],
  curr: WireSimState["entities"],
): WireEntitiesDelta | null {
  const prevMap = new Map(prev.map((e) => [e.entityId, e]));
  const currMap = new Map(curr.map((e) => [e.entityId, e]));

  const upserted: WireEntityDelta[] = [];
  const removed: EntityId[] = [];

  for (const e of curr) {
    const old = prevMap.get(e.entityId);
    const d = diffWireEntity(old, e);
    if (d !== null) {
      upserted.push(d);
    }
  }

  for (const e of prev) {
    if (!currMap.has(e.entityId)) {
      removed.push(e.entityId);
    }
  }

  if (upserted.length === 0 && removed.length === 0) return null;
  const delta: WireEntitiesDelta = {};
  if (upserted.length > 0) delta.upserted = upserted;
  if (removed.length > 0) delta.removed = removed;
  return delta;
}

function areProjectilesEqual(
  a: WireSimState["projectiles"][number],
  b: WireSimState["projectiles"][number],
): boolean {
  return (
    a.projectileId === b.projectileId &&
    a.abilityId === b.abilityId &&
    a.ownerSeat === b.ownerSeat &&
    a.ownerTeam === b.ownerTeam &&
    a.x === b.x &&
    a.y === b.y &&
    a.dx === b.dx &&
    a.dy === b.dy &&
    a.radius === b.radius
  );
}

function areWallsEqual(
  a: WireSimState["walls"][number],
  b: WireSimState["walls"][number],
): boolean {
  if (
    a.wallId !== b.wallId ||
    a.ownerSeat !== b.ownerSeat ||
    a.ownerTeam !== b.ownerTeam ||
    a.expiresAtTick !== b.expiresAtTick ||
    a.obstacles.length !== b.obstacles.length
  ) {
    return false;
  }
  for (let i = 0; i < a.obstacles.length; i++) {
    const oa = a.obstacles[i];
    const ob = b.obstacles[i];
    if (oa?.x !== ob?.x || oa?.y !== ob?.y || oa?.radius !== ob?.radius) {
      return false;
    }
  }
  return true;
}

export function diffProjectiles(
  prev: WireSimState["projectiles"],
  curr: WireSimState["projectiles"],
): WireProjectilesDelta | null {
  const prevMap = new Map(prev.map((p) => [p.projectileId, p]));
  const currMap = new Map(curr.map((p) => [p.projectileId, p]));

  const upserted: WireSimState["projectiles"][number][] = [];
  const removed: string[] = [];

  for (const p of curr) {
    const old = prevMap.get(p.projectileId);
    if (!old || !areProjectilesEqual(old, p)) {
      upserted.push(p);
    }
  }

  for (const p of prev) {
    if (!currMap.has(p.projectileId)) {
      removed.push(p.projectileId);
    }
  }

  if (upserted.length === 0 && removed.length === 0) return null;
  const delta: WireProjectilesDelta = {};
  if (upserted.length > 0) delta.upserted = upserted;
  if (removed.length > 0) delta.removed = removed;
  return delta;
}

export function diffWalls(
  prev: WireSimState["walls"],
  curr: WireSimState["walls"],
): WireWallsDelta | null {
  const prevMap = new Map(prev.map((w) => [w.wallId, w]));
  const currMap = new Map(curr.map((w) => [w.wallId, w]));

  const upserted: WireSimState["walls"][number][] = [];
  const removed: string[] = [];

  for (const w of curr) {
    const old = prevMap.get(w.wallId);
    if (!old || !areWallsEqual(old, w)) {
      upserted.push(w);
    }
  }

  for (const w of prev) {
    if (!currMap.has(w.wallId)) {
      removed.push(w.wallId);
    }
  }

  if (upserted.length === 0 && removed.length === 0) return null;
  const delta: WireWallsDelta = {};
  if (upserted.length > 0) delta.upserted = upserted;
  if (removed.length > 0) delta.removed = removed;
  return delta;
}

export function diffWireState(
  prev: WireSimState,
  curr: WireSimState,
  stateHash: StateHash,
): ServerDeltaMessage {
  const delta: ServerDeltaMessage = {
    type: "delta",
    tick: curr.tick,
    stateHash,
  };

  if (prev.phase !== curr.phase) {
    delta.phase = curr.phase;
  }
  if (prev.winner !== curr.winner) {
    delta.winner = curr.winner;
  }

  const entitiesDelta = diffEntities(prev.entities, curr.entities);
  if (entitiesDelta !== null) {
    delta.entities = entitiesDelta;
  }

  const projDelta = diffProjectiles(prev.projectiles, curr.projectiles);
  if (projDelta !== null) {
    delta.projectiles = projDelta;
  }

  const wallsDelta = diffWalls(prev.walls, curr.walls);
  if (wallsDelta !== null) {
    delta.walls = wallsDelta;
  }

  if (curr.events.length > 0) {
    delta.events = curr.events;
  }
  if (curr.castEvents.length > 0) {
    delta.castEvents = curr.castEvents;
  }
  if ((curr.meleeAttackEvents?.length ?? 0) > 0) {
    delta.meleeAttackEvents = curr.meleeAttackEvents;
  }
  if ((curr.projectileContacts?.length ?? 0) > 0) {
    delta.projectileContacts = curr.projectileContacts;
  }
  if (curr.chat.length > 0) {
    delta.chat = curr.chat;
  }

  return delta;
}

/**
 * Apply an entity delta onto a baseline entity state (#1055).
 * Pure function.
 */
export function applyEntityDelta(
  base: WireSimState["entities"][number] | undefined,
  delta: WireEntityDelta,
): WireSimState["entities"][number] {
  if (base === undefined) {
    // Brand new entity — delta contains full entity shape
    return {
      entityId: delta.entityId,
      seat: delta.seat ?? toSeatIndex(0),
      team: delta.team ?? toTeamIndex(0),
      x: delta.x ?? toUnits(0),
      y: delta.y ?? toUnits(0),
      facing: delta.facing ?? 0,
      hp: delta.hp ?? toHp(0),
      maxHp: delta.maxHp ?? toHp(0),
      alive: delta.alive ?? false,
      cast: delta.cast ?? null,
      cooldowns: delta.cooldowns ?? [],
      gcdReadyAtTick: delta.gcdReadyAtTick ?? toTick(0),
      targets: delta.targets ?? { hostile: null, friendly: null },
      auras: delta.auras ?? [],
      resource: delta.resource ?? null,
      points: delta.points ?? null,
      radius: delta.radius ?? toUnits(0),
      championId: delta.championId ?? ("" as WireSimState["entities"][number]["championId"]),
    };
  }

  return {
    entityId: base.entityId,
    seat: delta.seat ?? base.seat,
    team: delta.team ?? base.team,
    x: delta.x ?? base.x,
    y: delta.y ?? base.y,
    facing: delta.facing ?? base.facing,
    hp: delta.hp ?? base.hp,
    maxHp: delta.maxHp ?? base.maxHp,
    alive: delta.alive ?? base.alive,
    cast: delta.cast !== undefined ? delta.cast : base.cast,
    cooldowns: delta.cooldowns ?? base.cooldowns,
    gcdReadyAtTick: delta.gcdReadyAtTick ?? base.gcdReadyAtTick,
    targets: delta.targets ?? base.targets,
    auras: delta.auras ?? base.auras,
    resource: delta.resource !== undefined ? delta.resource : base.resource,
    points: delta.points !== undefined ? delta.points : base.points,
    radius: delta.radius ?? base.radius,
    championId: delta.championId ?? base.championId,
  };
}

/**
 * Reconstruct a complete WireSimState by applying a ServerDeltaMessage
 * onto the previous authoritative WireSimState (#1055). Pure.
 */
export function applyWireDelta(
  prev: WireSimState,
  delta: ServerDeltaMessage,
): WireSimState {
  let entities = prev.entities;
  if (delta.entities) {
    const entityMap = new Map(prev.entities.map((e) => [e.entityId, e]));
    if (delta.entities.removed) {
      for (const id of delta.entities.removed) {
        entityMap.delete(id);
      }
    }
    if (delta.entities.upserted) {
      for (const d of delta.entities.upserted) {
        const existing = entityMap.get(d.entityId);
        const updated = applyEntityDelta(existing, d);
        entityMap.set(d.entityId, updated);
      }
    }
    const result: WireSimState["entities"] = [];
    for (const e of prev.entities) {
      const updated = entityMap.get(e.entityId);
      if (updated) {
        result.push(updated);
        entityMap.delete(e.entityId);
      }
    }
    for (const newEntity of entityMap.values()) {
      result.push(newEntity);
    }
    entities = result.sort((a, b) =>
      a.seat !== b.seat ? a.seat - b.seat : a.entityId < b.entityId ? -1 : a.entityId > b.entityId ? 1 : 0,
    );
  }

  // Projectiles
  let projectiles = prev.projectiles;
  if (delta.projectiles) {
    const projMap = new Map(prev.projectiles.map((p) => [p.projectileId, p]));
    if (delta.projectiles.removed) {
      for (const id of delta.projectiles.removed) {
        projMap.delete(id);
      }
    }
    if (delta.projectiles.upserted) {
      for (const p of delta.projectiles.upserted) {
        projMap.set(p.projectileId, p);
      }
    }
    projectiles = Array.from(projMap.values()).sort((a, b) =>
      a.projectileId < b.projectileId ? -1 : a.projectileId > b.projectileId ? 1 : 0,
    );
  }

  // Walls
  let walls = prev.walls;
  if (delta.walls) {
    const wallMap = new Map(prev.walls.map((w) => [w.wallId, w]));
    if (delta.walls.removed) {
      for (const id of delta.walls.removed) {
        wallMap.delete(id);
      }
    }
    if (delta.walls.upserted) {
      for (const w of delta.walls.upserted) {
        wallMap.set(w.wallId, w);
      }
    }
    walls = Array.from(wallMap.values()).sort((a, b) =>
      a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : 0,
    );
  }

  return {
    tick: delta.tick,
    phase: delta.phase ?? prev.phase,
    winner: delta.winner !== undefined ? delta.winner : prev.winner,
    entities,
    projectiles,
    walls,
    events: delta.events ?? [],
    castEvents: delta.castEvents ?? [],
    meleeAttackEvents: delta.meleeAttackEvents,
    projectileContacts: delta.projectileContacts,
    chat: delta.chat ?? [],
  };
}
